import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { dashboardApi } from '../lib/api';
import { TrendingUp, TrendingDown, Wallet, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatMonthChart = (data) => {
    return data.map(item => ({
      ...item,
      month: new Date(item.month + '-01').toLocaleDateString('id-ID', { month: 'short', year: 'numeric' })
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Ringkasan kas keluar masuk perusahaan</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Current Balance */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Saldo Saat Ini</p>
                <h3 className="text-2xl font-bold text-gray-900">
                  {formatCurrency(stats?.current_balance || 0)}
                </h3>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Wallet className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          {/* Monthly Income */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Kas Masuk Bulan Ini</p>
                <h3 className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats?.monthly_income || 0)}
                </h3>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          {/* Monthly Expense */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Kas Keluar Bulan Ini</p>
                <h3 className="text-2xl font-bold text-red-600">
                  {formatCurrency(stats?.monthly_expense || 0)}
                </h3>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <TrendingDown className="text-red-600" size={24} />
              </div>
            </div>
          </div>

          {/* Net Cashflow */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">Net Cashflow</p>
                <h3 className={`text-2xl font-bold ${
                  (stats?.net_cashflow || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(stats?.net_cashflow || 0)}
                </h3>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Activity className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tren Kas Masuk vs Keluar (6 Bulan Terakhir)</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={formatMonthChart(stats?.monthly_chart || [])}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Legend />
              <Bar dataKey="income" fill="#10b981" name="Kas Masuk" />
              <Bar dataKey="expense" fill="#ef4444" name="Kas Keluar" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Balance per Account */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Saldo per Akun Kas</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(stats?.balance_by_account || []).map((acc) => (
              <div
                key={acc.name}
                data-testid={`account-balance-${acc.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="border border-gray-100 rounded-xl p-5 bg-gray-50"
              >
                <p className="text-sm text-gray-500 mb-1">{acc.name}</p>
                <h3 className={`text-xl font-bold ${acc.balance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                  {formatCurrency(acc.balance)}
                </h3>
                <div className="flex items-center gap-3 mt-2 text-xs">
                  <span className="text-green-600">Masuk: {formatCurrency(acc.income)}</span>
                  <span className="text-red-600">Keluar: {formatCurrency(acc.expense)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Transaksi Terbaru</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {stats?.recent_transactions?.length > 0 ? (
              stats.recent_transactions.map((txn) => (
                <div key={txn.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{txn.description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-gray-500">{txn.date}</span>
                        <span className="text-sm text-gray-400">•</span>
                        <span className="text-sm text-gray-500">{txn.category_name}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${
                        txn.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                Belum ada transaksi
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
